import { supabase } from '../../lib/supabase';
import type { Leaseholder } from '../../types';

export const supabaseLeaseholderService = {
  getSubleaseHolders: async () => {
    console.log('Fetching sub-lease holders only...'); // Debug log

    try {
      // First, get the lease_holder IDs from subleases
      const { data: subleaseData, error: subleaseError } = await supabase
        .from('subleases')
        .select('lease_holder')
        .not('lease_holder', 'is', null);
      
      if (subleaseError) {
        console.error('Error fetching sublease holder IDs:', subleaseError);
        throw subleaseError;
      }
      
      // Extract the lease_holder IDs
      const leaseHolderIds = subleaseData
        .map(sublease => sublease.lease_holder)
        .filter(id => id !== null && id !== undefined);
      
      if (leaseHolderIds.length === 0) {
        console.log('No lease holders found in subleases');
        return [];
      }
      
      // Get only leaseholders referenced as lease_holder in the subleases table
      const { data: subLeaseHolders, error: subLeaseHoldersError } = await supabase
        .from('leaseholders')
        .select(`
          holder_id,
          holder_name,
          holder_contact,
          holder_phone,
          holder_email,
          holder_position,
          holder_phy_add01,
          holder_phy_add02,
          holder_phy_sub,
          holder_phy_state,
          holder_phy_pcode,
          holder_post_add01,
          holder_post_add02,
          holder_post_sub,
          holder_post_state,
          holder_pst_pcode,
          holder_abn,
          holder_acn,
          holder_indcn,
          created_date,
          modified_date
        `)
        .in('holder_id', leaseHolderIds)
        .order('holder_name');

      if (subLeaseHoldersError) {
        console.error('Supabase error fetching sub-lease holders:', subLeaseHoldersError);
        throw subLeaseHoldersError;
      }

      // Then, get lot counts and indicative annual values from lot_lease_summary
      const { data: lotSummaries, error: lotSummariesError } = await supabase
        .from('lot_lease_summary')
        .select(`
          holder_name,
          lot_id,
          rental_amount,
          lease_rent_freq
        `);

      if (lotSummariesError) {
        console.error('Supabase error fetching lot_lease_summary:', lotSummariesError);
        throw lotSummariesError;
      }

      // Process the data to calculate lot counts and indicative annual values
      const leaseholderSummaries = new Map();
      
      lotSummaries.forEach(lot => {
        if (!lot.holder_name) return;
        
        if (!leaseholderSummaries.has(lot.holder_name)) {
          leaseholderSummaries.set(lot.holder_name, {
            lots: new Set(),
            annualValue: 0
          });
        }
        
        const summary = leaseholderSummaries.get(lot.holder_name);
        
        // Add lot to the set (to count unique lots)
        if (lot.lot_id) {
          summary.lots.add(lot.lot_id);
        }
        
        // Calculate annual value based on frequency
        if (lot.rental_amount) {
          let multiplier = 1;
          if (lot.lease_rent_freq === 'Quarterly') {
            multiplier = 4;
          }
          // For 'Annually' or 'When requested', multiplier remains 1
          
          summary.annualValue += (lot.rental_amount * multiplier);
        }
      });
      
      // Merge the calculated data with the leaseholders
      const enrichedLeaseholders = subLeaseHolders.map(leaseholder => {
        const summary = leaseholderSummaries.get(leaseholder.holder_name);
        
        return {
          ...leaseholder,
          lot_count: summary ? summary.lots.size : 0,
          indicative_annual_value: summary ? summary.annualValue : null
        };
      });

      console.log('Enriched sub-lease holders data:', enrichedLeaseholders); // Debug log
      
      return enrichedLeaseholders;
    } catch (err) {
      console.error('Error fetching sub-lease holders:', err);
      throw err;
    }
  },
  getAll: async () => {
    console.log('Fetching leaseholders...'); // Debug log

    try {
      // First, get all leaseholders
      const { data: leaseholders, error: leaseholdersError } = await supabase
        .from('leaseholders')
        .select(`
          holder_id,
          holder_name,
          holder_contact,
          holder_phone,
          holder_email,
          holder_position,
          holder_phy_add01,
          holder_phy_add02,
          holder_phy_sub,
          holder_phy_state,
          holder_phy_pcode,
          holder_post_add01,
          holder_post_add02,
          holder_post_sub,
          holder_post_state,
          holder_pst_pcode,
          holder_abn,
          holder_acn,
          holder_indcn,
          created_date,
          modified_date
        `)
        .order('holder_name');

      if (leaseholdersError) {
        console.error('Supabase error fetching leaseholders:', leaseholdersError);
        throw leaseholdersError;
      }

      // Then, get lot counts and indicative annual values from lot_lease_summary
      const { data: lotSummaries, error: lotSummariesError } = await supabase
        .from('lot_lease_summary')
        .select(`
          holder_name,
          lot_id,
          rental_amount,
          lease_rent_freq
        `);

      if (lotSummariesError) {
        console.error('Supabase error fetching lot_lease_summary:', lotSummariesError);
        throw lotSummariesError;
      }

      // Process the data to calculate lot counts and indicative annual values
      const leaseholderSummaries = new Map();
      
      lotSummaries.forEach(lot => {
        if (!lot.holder_name) return;
        
        if (!leaseholderSummaries.has(lot.holder_name)) {
          leaseholderSummaries.set(lot.holder_name, {
            lots: new Set(),
            annualValue: 0
          });
        }
        
        const summary = leaseholderSummaries.get(lot.holder_name);
        
        // Add lot to the set (to count unique lots)
        if (lot.lot_id) {
          summary.lots.add(lot.lot_id);
        }
        
        // Calculate annual value based on frequency
        if (lot.rental_amount) {
          let multiplier = 1;
          if (lot.lease_rent_freq === 'Quarterly') {
            multiplier = 4;
          }
          // For 'Annually' or 'When requested', multiplier remains 1
          
          summary.annualValue += (lot.rental_amount * multiplier);
        }
      });
      
      // Merge the calculated data with the leaseholders
      const enrichedLeaseholders = leaseholders.map(leaseholder => {
        const summary = leaseholderSummaries.get(leaseholder.holder_name);
        
        return {
          ...leaseholder,
          lot_count: summary ? summary.lots.size : 0,
          indicative_annual_value: summary ? summary.annualValue : null
        };
      });

      console.log('Enriched leaseholders data:', enrichedLeaseholders); // Debug log
      
      return enrichedLeaseholders;
    } catch (err) {
      console.error('Error fetching leaseholders:', err);
      throw err;
    }
  },

  getById: async (id: number) => {
    const { data, error } = await supabase
      .from('leaseholders')
      .select('*')
      .eq('holder_id', id)
      .single();

    if (error) throw error;
    return data;
  },

  create: async (leaseholder: Omit<Leaseholder, 'holder_id' | 'created_date' | 'modified_date'>) => {
    // Validate required fields
    if (!leaseholder.holder_name) {
      throw new Error('Business Name is required');
    }

    // Prepare data for insertion - ensure all fields are properly formatted
    // Convert empty strings to null for fields with database constraints
    const dataToInsert = {
      ...leaseholder,
      holder_phy_pcode: leaseholder.holder_phy_pcode || null,
      holder_pst_pcode: leaseholder.holder_pst_pcode || null,
      holder_phy_add01: leaseholder.holder_phy_add01 || null,
      holder_phy_add02: leaseholder.holder_phy_add02 || null,
      holder_phy_sub: leaseholder.holder_phy_sub || null,
      holder_phy_state: leaseholder.holder_phy_state || null,
      holder_post_add01: leaseholder.holder_post_add01 || null,
      holder_post_add02: leaseholder.holder_post_add02 || null,
      holder_post_sub: leaseholder.holder_post_sub || null,
      holder_post_state: leaseholder.holder_post_state || null,
      holder_contact: leaseholder.holder_contact || null,
      holder_phone: leaseholder.holder_phone || null,
      holder_email: leaseholder.holder_email || null,
      holder_position: leaseholder.holder_position || null
    };

    console.log('Creating leaseholder with data:', JSON.stringify(dataToInsert, null, 2));

    try {
      // First, get the maximum holder_id
      const { data: maxIdResult, error: maxIdError } = await supabase
        .from('leaseholders')
        .select('holder_id')
        .order('holder_id', { ascending: false })
        .limit(1);

      if (maxIdError) {
        console.error('Error getting max holder_id:', maxIdError);
        throw maxIdError;
      }

      // Calculate the next ID
      const nextId = maxIdResult && maxIdResult.length > 0 ? maxIdResult[0].holder_id + 1 : 1;
      
      // Insert with explicit ID
      const { data, error } = await supabase
        .from('leaseholders')
        .insert([{ ...dataToInsert, holder_id: nextId }])
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating leaseholder:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error in create leaseholder service:', err);
      throw err;
    }
  },

  update: async (id: number, updates: Partial<Omit<Leaseholder, 'holder_id' | 'created_date' | 'modified_date'>>) => {
    // Validate required fields if holder_name is being updated
    if (updates.holder_name !== undefined && !updates.holder_name) {
      throw new Error('Business Name is required');
    }

    // Prepare data for update - ensure all fields are properly formatted
    // Convert empty strings to null for fields with database constraints
    const dataToUpdate = {
      ...updates
    };

    // Convert empty strings to null for all fields that might have constraints
    if (dataToUpdate.holder_phy_pcode === '') dataToUpdate.holder_phy_pcode = null;
    if (dataToUpdate.holder_pst_pcode === '') dataToUpdate.holder_pst_pcode = null;
    if (dataToUpdate.holder_phy_add01 === '') dataToUpdate.holder_phy_add01 = null;
    if (dataToUpdate.holder_phy_add02 === '') dataToUpdate.holder_phy_add02 = null;
    if (dataToUpdate.holder_phy_sub === '') dataToUpdate.holder_phy_sub = null;
    if (dataToUpdate.holder_phy_state === '') dataToUpdate.holder_phy_state = null;
    if (dataToUpdate.holder_post_add01 === '') dataToUpdate.holder_post_add01 = null;
    if (dataToUpdate.holder_post_add02 === '') dataToUpdate.holder_post_add02 = null;
    if (dataToUpdate.holder_post_sub === '') dataToUpdate.holder_post_sub = null;
    if (dataToUpdate.holder_post_state === '') dataToUpdate.holder_post_state = null;
    if (dataToUpdate.holder_contact === '') dataToUpdate.holder_contact = null;
    if (dataToUpdate.holder_phone === '') dataToUpdate.holder_phone = null;
    if (dataToUpdate.holder_email === '') dataToUpdate.holder_email = null;
    if (dataToUpdate.holder_position === '') dataToUpdate.holder_position = null;

    console.log(`Updating leaseholder ${id} with data:`, JSON.stringify(dataToUpdate, null, 2));

    try {
      const { data, error } = await supabase
        .from('leaseholders')
        .update(dataToUpdate)
        .eq('holder_id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error updating leaseholder:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error(`Error in update leaseholder service for ID ${id}:`, err);
      throw err;
    }
  },

  delete: async (id: number) => {
    const { error } = await supabase
      .from('leaseholders')
      .delete()
      .eq('holder_id', id);

    if (error) throw error;
  }
};
